import { useMutation } from '@apollo/client';
import { Chip, Icon, IconButton, TableColumnType, useTable } from '@elipssolution/harfang';
import { mdiDelete, mdiSync } from '@mdi/js';
import { Stack } from '@mui/material';

import useArchivedCustomerFileDataSource from './hooks/useArchivedCustomerFileDataSource';
import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import SettingsTable from '../../../components/SettingsTable';
import { CustomerFileType, CustomerFileWithDomainType } from '../../../types/customerFile';
import { REMOVE_CUSTOMER_FILE } from '../../api/customerFile';
import { SYNCHRONIZE_CUSTOMER_FILE } from '../../api/synchonization';

const ArchivedCustomerFileTableSection = () => {
	const tableInstance = useTable();
	const { dataSource: archivedCustomerFileDataSource } = useArchivedCustomerFileDataSource();
	const { confirmAction } = useConfirmationDialogContext();

	const [synchronizeCustomerFile] = useMutation(SYNCHRONIZE_CUSTOMER_FILE);

	const handleSynchronizeButtonClick = (customerFileId: CustomerFileType['id']) =>
		synchronizeCustomerFile({
			variables: {
				customerFileId,
			},
		});

	const [removeCustomerFile] = useMutation(REMOVE_CUSTOMER_FILE);

	const handleRemoveCustomerFileButtonClick = async ({
		id,
		code,
		name,
	}: Pick<CustomerFileWithDomainType, 'id' | 'code' | 'name'>) => {
		const isRemovalConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer le dossier ${code} - ${name} ?`,
			confirmButtonDetails: {
				color: 'error',
				label: 'Supprimer',
			},
		});

		if (!isRemovalConfirmed) return null;

		const result = await removeCustomerFile({
			variables: {
				removeCustomerFileId: id,
			},
		});

		tableInstance.reload();

		return result;
	};

	const columns: TableColumnType<CustomerFileWithDomainType>[] = [
		{
			field: 'code',
			flexGrow: 0,
			key: 'code',
			sortable: true,
			title: 'Code',
			width: 80,
		},
		{
			field: 'name',
			key: 'name',
			sortable: true,
			title: 'Nom du dossier',
			width: 465,
		},
		{
			key: 'chip',
			render: ({ domain: { isDefault, name } }) => <Chip label={name} color={isDefault ? 'info' : 'default'} />,
			title: 'Domaine',
			width: 150,
		},
		{
			key: 'actions',
			align: 'right',
			flexGrow: 0,
			render: ({ id, name, code }) => (
				<Stack direction="row" gap={1}>
					<IconButton
						onClick={() => handleSynchronizeButtonClick(id)}
						size="small"
						tooltipContent="Synchroniser le dossier"
					>
						<Icon path={mdiSync} />
					</IconButton>
					<IconButton
						onClick={() => handleRemoveCustomerFileButtonClick({ id, name, code })}
						size="small"
						tooltipContent="Supprimer le dossier"
					>
						<Icon path={mdiDelete} />
					</IconButton>
				</Stack>
			),
			width: 72,
		},
	];

	return (
		<SettingsTable
			table={tableInstance}
			dataSource={archivedCustomerFileDataSource}
			tableColumns={columns}
			title="Dossiers archivés"
		/>
	);
};

export default ArchivedCustomerFileTableSection;
